import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, TextField, InputAdornment, Pagination,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { fetchOrders, getSellerInfo } from '../api';
import QRCode from 'qrcode';

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [billPrintDialog, setBillPrintDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [dateRangeDialog, setDateRangeDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Filter states
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const itemsPerPage = 10;

  useEffect(() => {
    loadOrders();
    // Load seller info
    getSellerInfo().then(r => setSellerInfo(r.data)).catch(() => setSellerInfo(null));
  }, []);

  // Generate QR code for Play Store app
  useEffect(() => {
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.OwnTrip.app';
    QRCode.toDataURL(playStoreUrl, { width: 140, margin: 1 })
      .then(url => setQrCodeDataUrl(url))
      .catch(err => console.error('Error generating QR code:', err));
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await fetchOrders();
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedOrders = filteredOrders.slice(startIndex, endIndex);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) return '';
    
    // Check if it's a multiple payment (contains comma or colon with numbers)
    if (paymentMethod.includes(',') || (paymentMethod.includes(':') && /\d/.test(paymentMethod))) {
      return 'Multi Payment Mode';
    }
    
    return paymentMethod.toUpperCase();
  };

  const getPaymentAmount = (order, selectedPaymentFilter) => {
    if (!order.payment_method || selectedPaymentFilter === 'all' || selectedPaymentFilter === 'multiple') {
      return parseFloat(order.total) || 0; // Ensure we return a valid number
    }

    // If it's a simple payment method (not multiple), return full amount
    if (!order.payment_method.includes(':')) {
      return parseFloat(order.total) || 0;
    }

    // Parse multiple payment methods
    const paymentParts = order.payment_method.split(',').map(p => p.trim());
    for (const part of paymentParts) {
      const [method, amount] = part.split(':').map(p => p.trim());
      if (method.toLowerCase() === selectedPaymentFilter.toLowerCase()) {
        return parseFloat(amount) || 0;
      }
    }

    return 0; // Return 0 if the payment method is not found
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDateRangeForFilter = useCallback((filter, customStart, customEnd) => {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    switch (filter) {
      case 'today':
        return { start: startOfToday, end: endOfToday };
      
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const startOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const endOfYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59);
        return { start: startOfYesterday, end: endOfYesterday };
      }
      
      case 'thisWeek': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        return { start: startOfWeek, end: endOfToday };
      }
      
      case 'lastWeek': {
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        const startOfLastWeek = new Date(lastWeek);
        startOfLastWeek.setDate(lastWeek.getDate() - lastWeek.getDay());
        startOfLastWeek.setHours(0, 0, 0, 0);
        const endOfLastWeek = new Date(startOfLastWeek);
        endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
        endOfLastWeek.setHours(23, 59, 59);
        return { start: startOfLastWeek, end: endOfLastWeek };
      }
      
      case 'thisMonth': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: startOfMonth, end: endOfToday };
      }
      
      case 'lastMonth': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        return { start: lastMonth, end: endOfLastMonth };
      }
      
      case 'custom':
        if (customStart && customEnd) {
          const start = new Date(customStart);
          const end = new Date(customEnd);
          end.setHours(23, 59, 59);
          return { start, end };
        }
        return null;
      
      default:
        return null;
    }
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = orders.filter(order => {
      // Search term filter
      const matchesSearch = order.id.toString().includes(searchTerm) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.customer_phone && order.customer_phone.includes(searchTerm));

      // Payment method filter
      let matchesPayment = true;
      if (paymentFilter !== 'all') {
        if (paymentFilter === 'multiple') {
          matchesPayment = order.payment_method && 
            (order.payment_method.includes(',') || 
             (order.payment_method.includes(':') && /\d/.test(order.payment_method)));
        } else {
          // Check if it's an exact match or part of multiple payments
          if (order.payment_method === paymentFilter) {
            matchesPayment = true;
          } else if (order.payment_method && order.payment_method.includes(':')) {
            // Check if the selected payment method is part of multiple payments
            const paymentParts = order.payment_method.split(',').map(p => p.trim());
            matchesPayment = paymentParts.some(part => 
              part.toLowerCase().startsWith(paymentFilter.toLowerCase() + ':')
            );
          } else {
            matchesPayment = false;
          }
        }
      }

      // Date range filter
      let matchesDate = true;
      if (dateFilter !== 'all') {
        const dateRange = getDateRangeForFilter(dateFilter, customStartDate, customEndDate);
        if (dateRange) {
          const orderDate = new Date(order.created_at);
          matchesDate = orderDate >= dateRange.start && orderDate <= dateRange.end;
        }
      }

      return matchesSearch && matchesPayment && matchesDate;
    });

    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [orders, searchTerm, paymentFilter, dateFilter, customStartDate, customEndDate, getDateRangeForFilter]);

  const clearFilters = () => {
    setPaymentFilter('all');
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSearchTerm('');
  };

  // Apply filters whenever dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  };

  const handlePrintBill = (order) => {
    setSelectedOrder(order);
    setBillPrintDialog(true);
  };

  const printBillOnly = () => {
    if (!selectedOrder) {
      alert('No order selected');
      return;
    }

    // Create pure HTML bill content with inline CSS
    const generateBillHTML = () => {
      const items = selectedOrder?.items || [];
      const totalAmount = items.reduce((sum, item) => {
        const mrp = Number(item.mrp || item.unit_price || 0);
        return sum + (mrp * Number(item.quantity || 1));
      }, 0);
      
  const subtotal = items.reduce((sum, item) => sum + Number(item.total_price || 0), 0);
  const discountAmount = Number(selectedOrder?.discount_amount || 0);
  const finalTotal = Number(selectedOrder?.total || 0);

  // Format dates consistently: M/D/YYYY h:mm:ss AM/PM
  const dateOpts = { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
  const billingDateStr = selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-US', dateOpts) : new Date().toLocaleString('en-US', dateOpts);
  const printDateStr = new Date().toLocaleString('en-US', dateOpts);

  // Sanitize payment display: if a file path was accidentally pasted (contains backslashes or slashes), extract the method before the path
  let paymentRaw = selectedOrder?.payment_method || selectedOrder?.payment || '';
  let paymentDisplay = paymentRaw ? String(paymentRaw) : 'CASH';
  // Normalize common accidental prefixes and cut off on backslash/forward slash
  paymentDisplay = paymentDisplay.replace(/^UPId/i, 'UPI');
  paymentDisplay = paymentDisplay.split(/\\|\//)[0];
  paymentDisplay = paymentDisplay.trim() || 'CASH';

      return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bill #${selectedOrder.id}</title>
            <meta charset="UTF-8">
            <style>
              /* Reset basics */
              *{margin:0;padding:0;box-sizing:border-box}

              /* Remove default page margins when printing */
              @page{margin:0;size:auto}

              html,body{width:100%;background:#ffffff;color:#000000}

              /* Receipt-friendly font stack: Helvetica/Arial for best printer support */
              body{
                font-family: 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
                margin:0;
                padding:0;
                background:#ffffff;
                color:#000000;
                font-size:13px;
                line-height:1.18;
                font-weight:400;
                -webkit-font-smoothing:none;
                text-rendering:optimizeSpeed;
                width:100%;
              }

              /* Container should use full width and no margins */
              .invoice-container{width:100%;padding:0;margin:0}

              /* Make sure every element prints in pure black on white */
              .invoice-container *{
                color:#000000 !important;
                background:transparent !important;
              }

              .invoice-container img{display:block;max-width:100%;height:auto;border:0}

              @media print{
                /* Ensure no margins around page in print */
                @page{margin:0}
                html,body{width:100% !important;max-width:none !important;margin:0 !important;padding:0 !important}
                body{font-size:12px !important;line-height:1.15 !important}
                .invoice-container{width:100% !important;margin:0 !important;padding:0 !important}

                /* Force black text and remove background effects */
                body, .invoice-container, .invoice-container *{
                  -webkit-print-color-adjust:exact !important;
                  color-adjust:exact !important;
                  color:#000000 !important;
                  background:transparent !important;
                }

                /* Remove image filters/shadows to preserve contrast */
                img{filter:none !important;box-shadow:none !important}
                *{ -webkit-font-smoothing:none !important; text-rendering:optimizeSpeed !important }
              }
            </style>
          </head>
          <body>
            <div class="invoice-container">
            <!-- Header -->
            <div style="text-align: center; margin: 0 0 4px 0; padding: 2px 0; border-bottom: 2px solid black; width: 100%;">
              <div style="font-size: 20px; margin: 0 0 2px 0; letter-spacing: 0.5px; text-transform: none; padding: 0; font-weight:700; font-family: 'Brush Script MT', 'Lucida Handwriting', 'Dancing Script', cursive; font-style: italic;">
                ${sellerInfo?.store_name || sellerInfo?.name || 'Store Name'}
              </div>
              ${sellerInfo?.formatted_address ? `<div style="font-size:12px;margin:0 0 1px 0;padding:0;">${sellerInfo.formatted_address}</div>` : ''}
              ${sellerInfo?.email || sellerInfo?.mobile ? `<div style="font-size:12px;margin:0 0 1px 0;padding:0;">${sellerInfo.email ? '' + sellerInfo.email : ''}${sellerInfo.mobile ? ' | ' + sellerInfo.mobile : ''}</div>` : ''}
            </div>
            <!-- Order Info -->
            <div style="margin: 0 0 3px 0; padding: 0 0 2px 0; border-bottom: 1px dashed black; width: 100%;">
              <div style="display: flex; gap:4px; justify-content: space-between; align-items: center; margin: 0 0 1px 0; width: 100%; padding: 0;">
                  <div style="font-size: 9px; width: 65%; padding: 0; margin: 0;">
                  Billing Date: ${billingDateStr}
                </div>
                <div style="font-size: 9px; width: 35%; text-align: right; padding: 0; margin: 0;">
                  Bill No: #${selectedOrder.id}
                </div>
              </div>
              <div style="font-size: 9px; margin: 0 0 1px 0; width: 100%; padding: 0;">
                Bill print Date: ${printDateStr}
              </div>
              ${selectedOrder.customer_name ? `
                <div style="font-size: 9px; margin: 0 0 1px 0; width: 100%; padding: 0;">
                  Customer: ${selectedOrder.customer_name}
                </div>
              ` : ''}
              <div style="font-size: 9px; margin: 0; width: 100%; padding: 0;">
                Payment: ${paymentDisplay}
              </div>
            </div>

            <!-- Items Header -->
            <div style="margin: 0; padding-bottom: 2px; border-bottom: 1px solid black; width: 100%;">
              <div style="display: flex; gap:4px; justify-content: space-between; align-items: center; width: 100%; padding: 0; margin: 0;">
                <div style="width: 6%; text-align: left; font-size: 11px; padding: 0; margin: 0;">Sl</div>
                <div style="width: 44%; text-align: left; font-size: 11px; padding: 0; margin: 0;">Item</div>
                <div style="width: 13%; text-align: center; font-size: 11px; padding: 0; margin: 0;">MRP</div>
                <div style="width: 8%; text-align: center; font-size: 11px; padding: 0; margin: 0;">Dsc%</div>
                <div style="width: 12%; text-align: center; font-size: 11px; padding: 0; margin: 0;">Qty</div>
                <div style="width: 17%; text-align: right; font-size: 11px; padding: 0; margin: 0;">Total</div>
              </div>
            </div>

            <!-- Items -->
            ${items.map((item, index) => {
              const mrp = Number(item.mrp || 0);
              const totalPrice = Number(item.total_price || 0);
              const quantity = Number(item.quantity || 1);
              const totalMRP = mrp * quantity;
              const discountAmount = totalMRP - totalPrice;
              const discountPercent = totalMRP > 0 ? ((discountAmount / totalMRP) * 100).toFixed(0) : 0;
              
              const discountedRate = mrp - (mrp * discountPercent / 100);
              
              return `
                <div style="margin: 0; padding: 1px 0; width: 100%;">
                  <!-- Item Row -->
                  <div style="display: flex; gap:4px; justify-content: space-between; align-items: flex-start; width: 100%; margin: 0; padding: 0;">
                    <div style="width: 6%; text-align: left; font-size: 10px; padding: 0; margin: 0;">${index + 1}</div>
                    <div style="width: 44%; text-align: left; font-size: 10px; line-height: 1.1; word-wrap: break-word; padding: 0; margin: 0;">
                      ${item.product_name}${(typeof item.product_id === 'string' && item.product_id.startsWith('0')) ? ' (Loose)' : ''}
                      ${discountPercent > 0 ? `<br><span style="font-size: 9px; color: black;">@ ₹${discountedRate.toFixed(2)}/${item.unit_short_code || 'pc'}</span>` : ''}
                    </div>
                    <div style="width: 13%; text-align: center; font-size: 10px; padding: 0; margin: 0;">
                      ₹${mrp.toFixed(2)}
                    </div>
                    <div style="width: 8%; text-align: center; font-size: 10px; padding: 0; margin: 0;">
                      ${discountPercent}%
                    </div>
                    <div style="width: 12%; text-align: center; font-size: 10px; padding: 0; margin: 0;">
                      ${(typeof item.product_id === 'string' && item.product_id.startsWith('0')) ? item.quantity : Math.round(item.quantity)} ${item.unit_short_code || 'pc'}
                    </div>
                    <div style="width: 17%; text-align: right; font-size: 10px; padding: 0; margin: 0;">
                      ₹${totalPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
              `;
            }).join('')}

            <!-- Separator -->
            <div style="border-top: 1px dashed black; margin: 2px 0;"></div>

            <!-- Totals -->
            <div style="margin: 0 0 3px 0; padding: 2px 0 0 0; border-top: 1px dashed black; width: 100%;">
              <div style="display: flex; gap:4px; justify-content: space-between; margin: 0 0 1px 0; width: 100%; padding: 0;">
                <span style="font-size: 10px; width: 70%; padding: 0; margin: 0;">Total Amount:</span>
                <span style="font-size: 10px; width: 30%; text-align: right; padding: 0; margin: 0;">₹${totalAmount.toFixed(2)}</span>
              </div>

              ${selectedOrder.discount_percentage > 0 ? `
                <div style="display: flex; gap:4px; justify-content: space-between; margin: 0 0 1px 0; width: 100%; padding: 0;">
                  <span style="font-size: 10px; width: 70%; padding: 0; margin: 0;">Discount (${selectedOrder.discount_percentage}%):</span>
                  <span style="font-size: 10px; width: 30%; text-align: right; padding: 0; margin: 0;">-₹${discountAmount.toFixed(2)}</span>
                </div>
              ` : ''}

              <div style="display: flex; gap:4px; justify-content: space-between; margin: 0 0 1px 0; width: 100%; padding: 0;">
                <span style="font-size: 10px; width: 70%; padding: 0; margin: 0;">Subtotal:</span>
                <span style="font-size: 10px; width: 30%; text-align: right; padding: 0; margin: 0;">₹${subtotal.toFixed(2)}</span>
              </div>

              ${selectedOrder.additional_charges?.map(charge => `
                <div style="display: flex; gap:4px; justify-content: space-between; margin: 0 0 1px 0; width: 100%; padding: 0;">
                  <span style="font-size: 10px; width: 70%; padding: 0; margin: 0;">${charge.name}:</span>
                  <span style="font-size: 10px; width: 30%; text-align: right; padding: 0; margin: 0;">₹${Number(charge.amount).toFixed(2)}</span>
                </div>
              `).join('') || ''}

              <div style="border-top: 1px dashed black; margin: 2px 0 0 0; padding: 2px 0 0 0; width: 100%;">
                <div style="display: flex; gap:4px; justify-content: space-between; width: 100%; margin: 0; padding: 0;">
                  <span style="font-size: 12px; width: 70%; padding: 0; margin: 0;">Final Payable:</span>
                  <span style="font-size: 12px; width: 30%; text-align: right; padding: 0; margin: 0;">₹${finalTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div style="text-align: center; padding: 3px 0 0 0; border-top: 2px solid black; width: 100%; margin: 0;">
              <div style="font-size: 14px; margin: 0 0 2px 0; letter-spacing: 1px; padding: 0;">*** THANK YOU ***</div>
              <div style="font-size: 12px; margin: 0 0 3px 0; padding: 0;">Visit Again Soon</div>
              
              ${qrCodeDataUrl ? `
                <div style="display: flex; gap:4px; justify-content: center; margin: 0 0 2px 0; width: 100%; padding: 0;">
                  <img src="${qrCodeDataUrl}" alt="Download our app" style="width: 60px; height: 60px; object-fit: contain; border: 1px solid black; margin: 0; padding: 0;" />
                </div>
                <div style="font-size: 10px; margin: 0 0 1px 0; padding: 0;">Scan for Mobile App</div>
                <div style="font-size: 10px; margin: 0 0 8px 0; padding: 0;">Order Online</div>
                <hr>
                <div style="font-size: 8px; margin: 0 0 8px 0; padding: 0;">Powered by : OwnTrip (OPC) Pvt. Ltd.</div>
              <hr>
              ` : ''}
              
              <!-- Print spacing for clean cut -->
              <div style="margin: 8px 0 0 0; padding: 0;">&nbsp;</div>
              <div style="margin: 0; padding: 0;">&nbsp;</div>
              <div style="margin: 0; padding: 0;">&nbsp;</div>
            </div>

            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 250);
              }
            </script>
            </div>
          </body>
        </html>
      `;
    };

    // Create and open print window
    const printWindow = window.open('', '_blank', 'width=400,height=600,scrollbars=yes');
    printWindow.document.write(generateBillHTML());
    printWindow.document.close();
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const downloadTodayOrderReport = () => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    // Filter orders for today
    const todayOrders = filteredOrders.filter(order => {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      return orderDate === today;
    });

    // Create CSV content
    const headers = ['Order ID', 'Date', 'Customer', 'Phone', 'Items', 'Total', 'Payment Method', 'Status'];
    const csvContent = [
      headers.join(','),
      ...todayOrders.map(order => [
        order.id,
        formatDate(order.created_at),
        order.customer_name || 'Walk-in',
        order.customer_phone || '',
        order.items?.length || 0,
        getPaymentAmount(order, paymentFilter),
        formatPaymentMethod(order.payment_method),
        order.status || 'completed'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `today_orders_report_${today}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadDateRangeReport = (startDate, endDate) => {
    // Filter orders within date range
    const dateRangeOrders = filteredOrders.filter(order => {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      return orderDate >= startDate && orderDate <= endDate;
    });

    // Create CSV content
    const headers = ['Order ID', 'Date', 'Customer', 'Phone', 'Items', 'Total', 'Payment Method', 'Status'];
    const csvContent = [
      headers.join(','),
      ...dateRangeOrders.map(order => [
        order.id,
        formatDate(order.created_at),
        order.customer_name || 'Walk-in',
        order.customer_phone || '',
        order.items?.length || 0,
        getPaymentAmount(order, paymentFilter),
        formatPaymentMethod(order.payment_method),
        order.status || 'completed'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orders_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <Box>
          <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>Loading orders...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="orders-page" sx={{ mt: 2, mb: 2, px: 2 }}>
      {/* Unified Modern Card - All Elements Combined */}
      <Paper className="orders-shell" sx={{
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
        borderRadius: 1
      }}>
        {/* Header Section */}
        <Box className="orders-header" sx={{
          py: 1.5,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}>
                Billing History
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {filteredOrders.length} orders • {displayedOrders.length} showing
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Search and Actions Section */}
        <Box className="orders-toolbar" sx={{
          py: 1.5,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            {/* Left Side - Search and Filters */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search by ID, name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem'
                  }
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Payment</InputLabel>
                <Select
                  value={paymentFilter}
                  label="Payment"
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="multiple">Multiple Payments</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Range"
                  onChange={(e) => setDateFilter(e.target.value)}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="all">All Dates</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="lastWeek">Last Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>

              {(paymentFilter !== 'all' || dateFilter !== 'all' || searchTerm) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FilterIcon sx={{ fontSize: '0.9rem' }} />}
                  onClick={clearFilters}
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    borderColor: '#f57c00',
                    color: '#f57c00',
                    '&:hover': {
                      borderColor: '#e65100',
                      bgcolor: 'rgba(245, 124, 0, 0.04)'
                    }
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            {/* Right Side - Download Actions */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={downloadTodayOrderReport}
                disabled={filteredOrders.length === 0}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  borderColor: '#43a047',
                  color: '#43a047',
                  '&:hover': {
                    borderColor: '#2e7d32',
                    bgcolor: 'rgba(67, 160, 71, 0.04)'
                  }
                }}
              >
                Today
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => setDateRangeDialog(true)}
                disabled={filteredOrders.length === 0}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  bgcolor: '#1976d2',
                  '&:hover': {
                    bgcolor: '#1565c0'
                  }
                }}
              >
                Range
              </Button>
            </Box>
          </Box>
          
          {/* Custom Date Range - Show below when custom is selected */}
          {dateFilter === 'custom' && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem'
                  }
                }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8rem'
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Active Filters Summary */}
        {(paymentFilter !== 'all' || dateFilter !== 'all') && (
          <Box sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
                Active Filters:
              </Typography>
              {paymentFilter !== 'all' && (
                <Chip 
                  size="small" 
                  label={`Payment: ${paymentFilter === 'multiple' ? 'Multi Payment' : paymentFilter.toUpperCase()}`}
                  onDelete={() => setPaymentFilter('all')}
                  sx={{ 
                    height: '22px', 
                    fontSize: '0.7rem',
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    '& .MuiChip-deleteIcon': { fontSize: '14px' }
                  }}
                />
              )}
              {dateFilter !== 'all' && (
                <Chip 
                  size="small" 
                  label={`Date: ${dateFilter === 'custom' ? 'Custom Range' : dateFilter.replace(/([A-Z])/g, ' $1').trim()}`}
                  onDelete={() => {
                    setDateFilter('all');
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                  sx={{ 
                    height: '22px', 
                    fontSize: '0.7rem',
                    bgcolor: '#e8f5e9',
                    color: '#43a047',
                    '& .MuiChip-deleteIcon': { fontSize: '14px' }
                  }}
                />
              )}
              <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 'auto' }}>
                Showing {filteredOrders.length} of {orders.length} orders
              </Typography>
            </Box>
          </Box>
        )}

      {/* Ultra-Compact Modern Table */}
      {/* Table Section */}
      <Box className="orders-table" sx={{ p: 0 }}>
        <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{
                bgcolor: '#f5f5f5',
                '& .MuiTableCell-head': {
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  py: 1,
                  px: 1.5,
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }
              }}>
                <TableCell>ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="right">
                  {paymentFilter !== 'all' && paymentFilter !== 'multiple' 
                    ? `${paymentFilter.toUpperCase()} Amount` 
                    : 'Total'
                  }
                </TableCell>
                <TableCell align="center">Payment</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{
                    textAlign: 'center',
                    py: 4,
                    borderBottom: 'none'
                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', fontWeight: 500 }}>
                        No orders found
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {searchTerm ? 'Try adjusting your search terms' : 'Orders will appear here'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                displayedOrders.map((order) => (
                  <TableRow key={order.id} sx={{
                    '&:hover': {
                      bgcolor: 'action.hover'
                    },
                    '& .MuiTableCell-root': {
                      py: 1,
                      px: 1.5,
                      fontSize: '0.8rem',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }
                  }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>
                      #{order.id}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell>
                      {order.customer_name ? (
                        <Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                            {order.customer_name}
                          </Typography>
                          {order.customer_phone && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              {order.customer_phone}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>
                          Walk-in
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${order.items?.length || 0}`}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                          bgcolor: '#e3f2fd',
                          color: '#1976d2'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#43a047' }}>
                      ₹{Number(getPaymentAmount(order, paymentFilter)).toFixed(2)}
                      {paymentFilter !== 'all' && paymentFilter !== 'multiple' && order.payment_method && order.payment_method.includes(':') && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                          of ₹{Number(order.total).toFixed(2)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatPaymentMethod(order.payment_method)}
                        size="small"
                        color={order.payment_method === 'cash' ? 'success' : 'primary'}
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={(order.status || 'completed').toUpperCase()}
                        size="small"
                        color={getStatusColor(order.status || 'completed')}
                        sx={{
                          height: '20px',
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order)}
                          title="View Details"
                          sx={{
                            p: 0.5,
                            color: '#1976d2',
                            '&:hover': {
                              bgcolor: '#e3f2fd'
                            }
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handlePrintBill(order)}
                          title="Print Bill"
                          sx={{
                            p: 0.5,
                            color: '#00acc1',
                            '&:hover': {
                              bgcolor: '#e0f7fa'
                            }
                          }}
                        >
                          <PrintIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Summary Section for Filtered Amounts */}
        {filteredOrders.length > 0 && paymentFilter !== 'all' && paymentFilter !== 'multiple' && (
          <Box sx={{
            py: 1,
            px: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Showing {paymentFilter.toUpperCase()} amounts from {filteredOrders.length} orders
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#43a047' }}>
                Total {paymentFilter.toUpperCase()}: ₹
                {(filteredOrders.reduce((sum, order) => {
                  const amount = getPaymentAmount(order, paymentFilter);
                  return sum + (isNaN(amount) ? 0 : amount);
                }, 0) || 0).toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                (Page: ₹{(displayedOrders.reduce((sum, order) => {
                  const amount = getPaymentAmount(order, paymentFilter);
                  return sum + (isNaN(amount) ? 0 : amount);
                }, 0) || 0).toFixed(2)})
              </Typography>
            </Box>
          </Box>
        )}

        {/* Pagination Section */}
        {filteredOrders.length > itemsPerPage && (
          <Box sx={{
            py: 1,
            px: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Pagination
              count={Math.ceil(filteredOrders.length / itemsPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.75rem'
                }
              }}
            />
          </Box>
        )}
      </Paper>
      <Dialog
        open={orderDetailsDialog}
        onClose={() => setOrderDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#1976d2',
          color: 'white',
          py: 1.5,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Order #{selectedOrder?.id} - Details
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
            Order Information
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedOrder && (
            <Box sx={{ p: 2 }}>
              {/* Compact Info Cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
                <Paper sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '3px solid #1976d2',
                  boxShadow: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                    Order Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Date:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{formatDate(selectedOrder.created_at)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Status:</Typography>
                      <Chip
                        label={(selectedOrder.status || 'completed').toUpperCase()}
                        size="small"
                        color={getStatusColor(selectedOrder.status || 'completed')}
                        sx={{ height: '18px', fontSize: '0.65rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Payment:</Typography>
                      <Chip
                        label={selectedOrder.payment_method?.toUpperCase()}
                        size="small"
                        color={selectedOrder.payment_method === 'cash' ? 'success' : 'primary'}
                        sx={{ height: '18px', fontSize: '0.65rem' }}
                      />
                    </Box>
                  </Box>
                </Paper>

                <Paper sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '3px solid #f57c00',
                  boxShadow: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                    Customer Info
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Name:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {selectedOrder.customer_name || 'Walk-in'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Mobile:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        {selectedOrder.customer_phone || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Items:</Typography>
                      <Chip
                        label={`${selectedOrder.items?.length || 0} items`}
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '0.65rem',
                          bgcolor: '#fff3e0',
                          color: '#f57c00'
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>

                <Paper sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '3px solid #43a047',
                  boxShadow: 1
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
                    Order Summary
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Amount:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1976d2' }}>
                        ₹{(Number(selectedOrder.total) + Number(selectedOrder.discount_amount || 0)).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                        Discount ({selectedOrder.discount_percentage || 0}%):
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#d32f2f' }}>
                        ₹{Number(selectedOrder.discount_amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Payable:</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#43a047' }}>
                        ₹{Number(selectedOrder.total).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Ultra-Compact Items Table */}
              <Paper sx={{
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1
              }}>
                <Box sx={{
                  bgcolor: '#43a047',
                  color: 'white',
                  py: 1,
                  px: 1.5
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    Order Items
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Item</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Measurement</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Qty</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>MRP</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Disc%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Sale Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.7rem', py: 0.75, px: 1 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => {
                      const mrp = Number(item.mrp || 0);
                      const totalPrice = Number(item.total_price || 0);
                      const quantity = Number(item.quantity || 1);
                      const salePrice = totalPrice / quantity;
                      const totalMRP = mrp * quantity;
                      const discountAmount = totalMRP - totalPrice;
                      const discountPercent = totalMRP > 0 ? ((discountAmount / totalMRP) * 100).toFixed(0) : 0;
                      
                      return (
                        <TableRow key={index} sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: '0.75rem', borderBottom: '1px solid', borderColor: 'divider' }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                {item.product_name}
                              </Typography>
                              {(item.is_loose_item === 1 || item.is_loose_item === true ||
                                item.product_id === null || item.product_id === undefined || Number(item.product_id) === 0 ||
                                (typeof item.product_id === 'string' && item.product_id.startsWith('0'))) && (
                                <Chip 
                                  label="Loose Items" 
                                  size="small" 
                                  sx={{ 
                                    // Matches the compact badge used in the POS cart.
                                    // styles.css pins chips with
                                    //   .enterprise-content .MuiChip-root {
                                    //     height: 24px !important; font-size: .7rem !important; }
                                    // so this compact size has to win with !important too.
                                    '&.MuiChip-sizeSmall': {
                                      height: '14px !important',
                                      fontSize: '0.5rem !important',
                                      borderRadius: '3px'
                                    },
                                    fontWeight: 600,
                                    bgcolor: '#f57c00', 
                                    color: 'white',
                                    flexShrink: 0,
                                    '& .MuiChip-label': { px: 0.4, lineHeight: '14px', whiteSpace: 'nowrap' }
                                  }} 
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              {item.measurement ? `${item.measurement} ${item.unit_short_code || item.unit_name}` : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem' }}>
                            {(typeof item.product_id === 'string' && item.product_id.startsWith('0')) ? 
                              item.quantity : 
                              Math.round(item.quantity)
                            }
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem' }}>₹{mrp.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem' }}>{discountPercent}%</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>₹{salePrice.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>₹{totalPrice.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{
                      bgcolor: '#43a047',
                      '& .MuiTableCell-root': {
                        borderBottom: 'none',
                        color: 'white',
                        py: 0.75,
                        fontWeight: 600
                      }
                    }}>
                      <TableCell colSpan={6} align="right" sx={{ fontSize: '0.85rem' }}>
                        Total Amount:
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.9rem' }}>
                        ₹{Number(selectedOrder.total).toFixed(2)}
                      </TableCell>
                    </TableRow>
                    {/* Additional Charges */}
                    {selectedOrder.additional_charges && selectedOrder.additional_charges.length > 0 && (
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell colSpan={7} sx={{ py: 0.5, borderBottom: '2px solid #43a047' }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f', textAlign: 'center' }}>
                            Additional Charges
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {selectedOrder.additional_charges?.map((charge, index) => (
                      <TableRow key={`charge-${index}`} sx={{
                        bgcolor: '#fff3e0',
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          py: 0.75
                        }
                      }}>
                        <TableCell colSpan={6} align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#d32f2f' }}>
                          {charge.name}:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#d32f2f' }}>
                          ₹{Number(charge.amount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          py: 1,
          px: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          gap: 1
        }}>
          <Button
            onClick={() => setOrderDetailsDialog(false)}
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.5
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={() => {
              setOrderDetailsDialog(false);
              setBillPrintDialog(true);
            }}
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.5,
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            Print Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bill Print Dialog - Reuse the same bill printing logic */}
      <Dialog
        open={billPrintDialog}
        onClose={() => setBillPrintDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
            maxWidth: '400px',
            fontFamily: 'monospace',
            '@media print': {
              boxShadow: 'none',
              border: 'none',
              borderRadius: 0,
              maxWidth: 'none',
              width: '100%',
              height: 'auto',
              position: 'static'
            }
          }
        }}
        data-print="bill"
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          py: 1.5,
          px: 2,
          textAlign: 'center',
          position: 'relative',
          '@media print': {
            display: 'none'
          }
        }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: '1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            position: 'relative',
            zIndex: 1,
            fontFamily: 'monospace'
          }}>
            🧾 THERMAL BILL
          </Typography>
        </DialogTitle>
        <DialogContent sx={{
          p: 0.5,
          background: '#f8f9fa',
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          lineHeight: 1.1,
          '@media print': {
            background: 'white',
            p: 0
          }
        }}>
          <Box sx={{
            background: 'white',
            mx: 0,
            my: 0,
            borderRadius: 0,
            border: 'none',
            p: 0.5,
            '@media print': {
              mx: 0,
              my: 0,
              border: 'none',
              borderRadius: 0,
              p: 0,
              fontSize: '0.7rem',
              lineHeight: 1.1
            }
          }}
          className="bill-print-content"
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 0.3, pb: 0.3, borderBottom: '1px dashed #ccc', '@media print': { mb: 0.2, pb: 0.2 } }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5 }}>
                {sellerInfo?.store_name || 'Store Name'}
              </Typography>
              {sellerInfo?.email && (
                <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>
                  {sellerInfo.email}
                </Typography>
              )}
              {sellerInfo?.mobile && (
                <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>
                  {sellerInfo.mobile}
                </Typography>
              )}
            </Box>

            {/* Order Info */}
            <Box sx={{ mb: 0.5, pb: 0.5, borderBottom: '1px dashed #ccc', '@media print': { mb: 0.3, pb: 0.3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem' }}>
                  Billing Date: {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleDateString() + ' ' + new Date(selectedOrder.created_at).toLocaleTimeString() : new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString()}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                  Bill No: #{selectedOrder?.id}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.7rem' }}>
                Bill print Date: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
              </Typography>
              {selectedOrder?.customer_name && (
                <Typography sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                  Customer: {selectedOrder.customer_name}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.7rem' }}>
                Payment: {selectedOrder?.payment_method?.toUpperCase()}
              </Typography>
            </Box>

            {/* Items Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3, pb: 0.3, borderBottom: '1px solid #ccc', '@media print': { mb: 0.2, pb: 0.2 } }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', flex: 0.5, textAlign: 'center' }}>SNo.</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', flex: 2 }}>Item</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 1 }}>MRP</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 0.8 }}>Disc%</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'center', flex: 0.8 }}>Qty</Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold', textAlign: 'right', flex: 1 }}>Total</Typography>
            </Box>

            {/* Items */}
            {selectedOrder?.items?.map((item, index) => {
              const mrp = Number(item.mrp || 0);
              const totalPrice = Number(item.total_price || 0);
              const quantity = Number(item.quantity || 1);
              const totalMRP = mrp * quantity;
              const discountAmount = totalMRP - totalPrice;
              const discountPercent = totalMRP > 0 ? ((discountAmount / totalMRP) * 100).toFixed(0) : 0;
              
              return (
                <Box key={index} sx={{ mb: 0.3, '@media print': { mb: 0.2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', flex: 0.5, textAlign: 'center' }}>{index + 1}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.product_name}
                      {(typeof item.product_id === 'string' && item.product_id.startsWith('0')) && ' (Loose)'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 1 }}>₹{mrp.toFixed(2)}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 0.8 }}>{discountPercent}%</Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', flex: 0.8 }}>
                      {(typeof item.product_id === 'string' && item.product_id.startsWith('0')) ? 
                        item.quantity : 
                        Math.round(item.quantity)
                      }{item.unit_short_code || item.unit_name ? ` ${item.unit_short_code || item.unit_name}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', textAlign: 'right', flex: 1 }}>₹{totalPrice.toFixed(2)}</Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Separator */}
            <Box sx={{ borderTop: '1px dashed #ccc', my: 0.3, '@media print': { my: 0.2 } }} />

            {/* Totals */}
            <Box sx={{ mb: 0.5, '@media print': { mb: 0.3 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                <Typography sx={{ fontSize: '0.7rem' }}>Total Amount:</Typography>
                <Typography sx={{ fontSize: '0.7rem' }}>₹{selectedOrder?.items?.reduce((sum, item) => {
                  const mrp = Number(item.mrp || item.unit_price || 0);
                  return sum + (mrp * Number(item.quantity || 1));
                }, 0).toFixed(2)}</Typography>
              </Box>

              {selectedOrder?.discount_percentage > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                  <Typography sx={{ fontSize: '0.7rem' }}>
                    Discount ({selectedOrder.discount_percentage}%):
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem' }}>-₹{Number(selectedOrder?.discount_amount || 0).toFixed(2)}</Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                <Typography sx={{ fontSize: '0.7rem' }}>Subtotal:</Typography>
                <Typography sx={{ fontSize: '0.7rem' }}>₹{selectedOrder?.items?.reduce((sum, item) => {
                  return sum + Number(item.total_price || 0);
                }, 0).toFixed(2)}</Typography>
              </Box>

              {selectedOrder?.additional_charges && selectedOrder.additional_charges.length > 0 && selectedOrder.additional_charges.map((charge, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2, '@media print': { mb: 0.1 } }}>
                  <Typography sx={{ fontSize: '0.7rem' }}>{charge.name}:</Typography>
                  <Typography sx={{ fontSize: '0.7rem' }}>₹{Number(charge.amount).toFixed(2)}</Typography>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.3, borderTop: '1px solid #ccc', mt: 0.3, '@media print': { pt: 0.2, mt: 0.2 } }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Final Payable:</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>₹{Number(selectedOrder?.total || 0).toFixed(2)}</Typography>
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', pt: 1, borderTop: '1px dashed #ccc' }}>
              <Typography sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 'bold' }}>Thank you for your business!</Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#666', mb: 1 }}>Visit again soon</Typography>
              
              {/* QR Code for Play Store */}
              {qrCodeDataUrl && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                  <Box
                    component="img"
                    src={qrCodeDataUrl}
                    alt="Download our app"
                    sx={{
                      width: '70px',
                      height: '70px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              <Typography sx={{ fontSize: '0.5rem', color: '#666', mb: 0.3 }}>Scan for our app</Typography>
              <Typography sx={{ fontSize: '0.5rem', color: '#666', fontWeight: 'bold' }}>Order online from mobile</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          pt: 0,
          '@media print': {
            display: 'none'
          }
        }}>
          <Button onClick={() => setBillPrintDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={printBillOnly}
          >
            Print Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Range Selection Dialog */}
      <Dialog
        open={dateRangeDialog}
        onClose={() => setDateRangeDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          fontSize: '1rem',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5
        }}>
          Select Date Range for Report
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          py: 1,
          px: 2
        }}>
          <Button 
            onClick={() => setDateRangeDialog(false)}
            sx={{ fontSize: '0.75rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (startDate && endDate) {
                downloadDateRangeReport(startDate, endDate);
                setDateRangeDialog(false);
                setStartDate('');
                setEndDate('');
              }
            }}
            disabled={!startDate || !endDate}
            sx={{ 
              fontSize: '0.75rem',
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            Download Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}